import React, { useEffect, useRef } from "react";
import { makeStyles, shorthands, tokens } from "@fluentui/react-components";
import { useNavigate } from "react-router-dom";
import { Title } from "../components/common/text/Title";
import { BodyText } from "../components/common/text/BodyText";
import { CtaButton } from "../components/common/input/CtaButton";
import { clarityEvent } from "../lib/clarity";
import { t } from "../i18n/i18n";

type ErrorProps = {
  /**
   * The technical failure string (GraphQL message, missing CMS single type,
   * "404", ...). It is never shown to visitors in production — it goes to
   * console.error for debugging and is rendered on screen only in dev.
   */
  error: string;
  variant?: "error" | "notFound";
};

const useStyles = makeStyles({
  wrapper: {
    // Must stay exactly colorBrandBackground (#263B52): the local logo PNG
    // has that colour baked in as an opaque background, so any other shade
    // makes the logo show up as a visible rectangle.
    backgroundColor: tokens.colorBrandBackground,
    minHeight: "100vh",
    // No global border-box reset in index.css, so the padding below would
    // otherwise be added on top of 100vh and force a scrollbar.
    boxSizing: "border-box",
    position: "relative",
    ...shorthands.overflow("hidden"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding(
      tokens.spacingVerticalXXXL,
      tokens.spacingHorizontalXL
    ),
  },
  // Decorative blobs sit in the corners only — a shape passing behind the
  // logo would be clipped by its opaque background into a visible rectangle.
  decoration: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
  },
  content: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: "620px",
    width: "100%",
  },
  logo: {
    width: "100%",
    maxWidth: "240px",
    height: "auto",
    marginBottom: tokens.spacingVerticalXXL,
    "@media screen and (max-width: 600px)": {
      maxWidth: "180px",
    },
  },
  headline: {
    color: "#FFFFFF",
  },
  body: {
    color: "rgba(255, 255, 255, 0.85)",
    maxWidth: "560px",
    marginTop: tokens.spacingVerticalM,
  },
  ctaRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.gap(tokens.spacingHorizontalL),
    marginTop: tokens.spacingVerticalXXL,
    // CtaButton bakes in large vertical margins for in-page use; here the row
    // owns the spacing so the stacked mobile buttons don't drift apart.
    "& > button": {
      ...shorthands.margin(0),
    },
    "@media screen and (max-width: 600px)": {
      flexDirection: "column",
      width: "100%",
    },
  },
  // CtaButton's "alternative" variant pairs white text with #FFB327 (~1.8:1,
  // fails WCAG AA). mergeClasses puts a passed className last, so this
  // overrides just the text colour for this page without touching the
  // shared component. Navy on orange is ~6.4:1.
  ctaOnOrange: {
    color: tokens.colorBrandBackground,
    ":hover": {
      color: tokens.colorBrandBackground,
    },
    ":active": {
      color: `${tokens.colorBrandBackground} !important`,
    },
  },
  devError: {
    color: "rgba(255, 255, 255, 0.6)",
    marginTop: tokens.spacingVerticalXXL,
    ...shorthands.overflow("hidden"),
    wordBreak: "break-word",
  },
});

export const Error = ({ error, variant = "error" }: ErrorProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const isNotFound = variant === "notFound";

  // React 18 StrictMode re-runs effects on the same fiber without resetting
  // refs, so this guard keeps the event to one per real occurrence.
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    // eslint-disable-next-line no-console
    console.error("[Error page]", error);
    clarityEvent(isNotFound ? "error_page_404_shown" : "error_page_shown");
  }, [error, isNotFound]);

  return (
    <div className={classes.wrapper} role="alert" aria-live="polite">
      <svg
        aria-hidden="true"
        className={classes.decoration}
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Top left */}
        <path
          d="M -40 40 C 60 -40, 220 -20, 250 110 C 280 240, 150 320, 30 280 C -90 240, -140 120, -40 40 Z"
          fill="rgba(13,27,46,0.55)"
        />
        <path
          d="M 120 -60 C 220 -120, 340 -60, 330 40 C 320 140, 200 170, 130 120 C 60 70, 40 -10, 120 -60 Z"
          fill="rgba(201,227,255,0.06)"
        />
        {/* Bottom right */}
        <path
          d="M 1480 860 C 1380 940, 1220 920, 1190 790 C 1160 660, 1290 580, 1410 620 C 1530 660, 1580 780, 1480 860 Z"
          fill="rgba(13,27,46,0.55)"
        />
        <path
          d="M 1160 960 C 1060 1000, 960 940, 980 850 C 1000 760, 1120 740, 1190 790 C 1260 840, 1250 920, 1160 960 Z"
          fill="rgba(201,227,255,0.05)"
        />
      </svg>

      <div className={classes.content}>
        <img
          className={classes.logo}
          src="/images/365RobustLogo.png"
          alt="365Robust"
          width={240}
          height={137}
        />

        <Title as="h1" align="center" className={classes.headline}>
          {isNotFound ? t("NotFoundHeadline") : t("ErrorHeadline")}
        </Title>

        <BodyText align="center" className={classes.body}>
          {isNotFound ? t("NotFoundBody") : t("ErrorBody")}
        </BodyText>

        <div className={classes.ctaRow}>
          <CtaButton
            buttonText={isNotFound ? t("ErrorSeePackages") : t("ErrorRetry")}
            color="alternative"
            className={classes.ctaOnOrange}
            onClick={() =>
              isNotFound ? navigate("/order") : window.location.reload()
            }
          />
          <CtaButton
            buttonText={t("GoToStart")}
            color="secondary"
            onClick={() => {
              // On the error variant a client-side navigate is a no-op when we
              // are already on "/" and would reuse Apollo's cached failure
              // elsewhere, so force a real reload. A 404 means the SPA itself
              // is healthy, so route normally there.
              if (isNotFound) navigate("/");
              else window.location.href = "/";
            }}
          />
        </div>

        {process.env.NODE_ENV === "development" && (
          <BodyText align="center" size={200} className={classes.devError}>
            {`${t("ErrorMessageLabel")} ${error}`}
          </BodyText>
        )}
      </div>
    </div>
  );
};
